import {
  Badge,
  BetaPill,
  Button,
  ElsterLogo,
  ModalV2,
  StepBlock,
  type StepStatus
} from '@components'
import { getInvoicingPageUrl } from '@root/constants'
import { useBusinessContext, useFeature } from '@context'
import { IVatPeriod, invalidateDashboardQueries, lockVatPeriod, reportVatPeriod } from '@query'
import { openInteractiveEuSalesReport } from '@pages/ReportsPage/interactive/interactiveReportsSession'
import { formatDate } from '@root/utils'
import dayjs from 'dayjs'
import React from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaCheckCircle, FaLock } from 'react-icons/fa'
import { Link, useHistory } from 'react-router-dom'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import ElsterReportModal from './ElsterReportModal'
import { EuRecapReportModal } from './EuRecapReportModal'
import VatReportModal from './VatReportModal'
import styled from 'styled-components'

interface Props {
  vatPeriod: IVatPeriod
}

type RecapStepState = 'blocked' | 'due' | 'overdue' | 'complete'

const RECAP_STEP_STATUS: Record<RecapStepState, StepStatus> = {
  blocked: 'blocked',
  due: 'current',
  overdue: 'warning',
  complete: 'complete'
}

const recapStepState = (vatPeriod: IVatPeriod): RecapStepState => {
  if (vatPeriod.is_recapitulative_reported) return 'complete'
  if (!vatPeriod.is_reported) return 'blocked'
  const overdue =
    Boolean(vatPeriod.recapitulative_due_date) &&
    dayjs().isAfter(dayjs(vatPeriod.recapitulative_due_date), 'day')
  return overdue ? 'overdue' : 'due'
}

export const VatPeriodSteps: React.FC<Props> = ({ vatPeriod }) => {
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const history = useHistory()
  const [showReportModal, setShowReportModal] = React.useState(false)
  const [showEuRecapModal, setShowEuRecapModal] = React.useState(false)
  const [t] = useTranslation()
  const { enabled: vatExternalReporting } = useFeature('vat_external_reporting')
  const { enabled: omaveroEnabled } = useFeature('omavero')
  const { enabled: elsterEnabled } = useFeature('elster')
  const useElsterModal = vatExternalReporting && elsterEnabled
  const useOmaveroModal = vatExternalReporting && omaveroEnabled
  const useExternalReportModal = useElsterModal || useOmaveroModal

  const lockMutation = useMutation({
    mutationFn: () => lockVatPeriod({ slug, vatPeriodId: vatPeriod.id }),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods', vatPeriod.id] })
        void invalidateDashboardQueries(queryClient, slug)
        toast.success(t('vatPage.lockSuccess'))
      },
      onError: error => {
        const { data } = (error as any) ?? {}
        if ('has_drafts' in data) {
          toast.error(t('vatPage.hasDrafts'))
        } else {
          toast.error(t('error.oho'))
        }
      }
    }
  })

  const reportMutation = useMutation({
    mutationFn: () => reportVatPeriod({ slug, vatPeriodId: vatPeriod.id }),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
        queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods', vatPeriod.id] })
        void invalidateDashboardQueries(queryClient, slug)
        toast.success(t('vatPage.reportSuccess'))
      },
      onError: () => {
        toast.error(t('error.oho'))
      }
    }
  })

  const LockDescription: React.FC = () => {
    return (
      <DescriptionBlock>
        <DescriptionText>
          <BodyText>{t('vatPage.verify')}</BodyText>
        </DescriptionText>
        <ButtonRow $align="left">
          <Button
            type="button"
            size="sm"
            intent="primary"
            icon={<FaLock size={12} />}
            disabled={vatPeriod.is_locked}
            onClick={() => lockMutation.mutate()}
          >
            {t('vatPage.lock')}
          </Button>
        </ButtonRow>
      </DescriptionBlock>
    )
  }

  const ReportedDescription: React.FC = () => {
    if (useElsterModal) {
      const taxToBePaid = vatPeriod.filing_tax_to_be_paid
      if (taxToBePaid != null && taxToBePaid <= 0) {
        return <BodyText $dimmed>{t('vatPage.elsterReport.creditResult')}</BodyText>
      }
      if (vatPeriod.purchase_invoice_id) {
        return (
          <DescriptionColumn>
            <DescriptionText>
              <BodyText $dimmed>
                {t('vatPage.elsterReport.invoiceCreated', {
                  date: formatDate(vatPeriod.due_date)
                })}
              </BodyText>
            </DescriptionText>
            <ButtonRow $align="left">
              <InvoiceLink
                to={getInvoicingPageUrl(slug, {
                  id: vatPeriod.purchase_invoice_id,
                  type: 'purchase'
                })}
              >
                {t('vatPage.elsterReport.openPurchaseInvoice')}
              </InvoiceLink>
            </ButtonRow>
          </DescriptionColumn>
        )
      }
    }

    return <BodyText $dimmed>{t('vatPage.hasBeenReported')}</BodyText>
  }

  const AccountDescription: React.FC = () => {
    return (
      <DescriptionColumn>
        <DescriptionText>
          <BodyText>
            {useExternalReportModal
              ? t(
                  useElsterModal ? 'vatPage.descReportSendableElster' : 'vatPage.descReportSendable'
                )
              : t('vatPage.descReport')}
          </BodyText>
        </DescriptionText>
        <ButtonRow $align="left">
          <Button
            type="button"
            size="sm"
            intent="primary"
            icon={<FaCheckCircle size={12} />}
            disabled={vatPeriod.is_reported}
            onClick={() => {
              useExternalReportModal ? setShowReportModal(true) : reportMutation.mutate()
            }}
          >
            {useExternalReportModal ? t('vatPage.reportPeriod') : t('vatPage.markReported')}
          </Button>
        </ButtonRow>
      </DescriptionColumn>
    )
  }

  const RecapDescription: React.FC<{ isOverdue: boolean }> = ({ isOverdue }) => {
    return (
      <DescriptionColumn>
        <DescriptionText>
          <BodyText>
            {isOverdue
              ? t('vatPage.euRecap.overdueDesc', {
                  dueDate: vatPeriod.recapitulative_due_date
                    ? formatDate(vatPeriod.recapitulative_due_date)
                    : ''
                })
              : t('vatPage.euRecap.desc')}
          </BodyText>
        </DescriptionText>
        <ButtonRow $align="left">
          <Button
            type="button"
            size="sm"
            intent="primary"
            icon={<FaCheckCircle size={12} />}
            disabled={vatPeriod.is_recapitulative_reported}
            onClick={() => setShowEuRecapModal(true)}
          >
            {t('vatPage.euRecap.reportButton')}
          </Button>
          <Button
            type="button"
            size="sm"
            intent="default"
            onClick={() => openInteractiveEuSalesReport(history, slug)}
          >
            {t('vatPage.euRecap.viewReport')}
          </Button>
        </ButtonRow>
      </DescriptionColumn>
    )
  }

  const showEuRecapStep = Boolean(vatPeriod.has_eu_sales)
  const recapState = recapStepState(vatPeriod)
  const recapStatus = RECAP_STEP_STATUS[recapState]

  const recapTitle = (
    <>
      {t('vatPage.euRecap.stepTitle')}
      {recapState === 'blocked' ? (
        <Badge type="default">{t('vatPage.euRecap.badgeEu')}</Badge>
      ) : recapState === 'overdue' ? (
        <Badge type="warning">{t('vatPage.euRecap.overdueBadge')}</Badge>
      ) : recapState === 'due' ? (
        <Badge type="info">
          {t('vatPage.euRecap.dueBadge', {
            date: vatPeriod.recapitulative_due_date
              ? formatDate(vatPeriod.recapitulative_due_date)
              : ''
          })}
        </Badge>
      ) : null}
    </>
  )

  return (
    <>
      <StepBlock
        title={t('vatPage.waitForEnd')}
        isBlocked={false}
        descComplete={
          <BodyText $dimmed>
            {t('vatPage.periodHasEnded', { date: formatDate(vatPeriod.end_date) })}
          </BodyText>
        }
        isCompleted={vatPeriod.has_ended}
      />

      <StepBlock
        title={t('vatPage.report')}
        descCurrent={<AccountDescription />}
        descComplete={<ReportedDescription />}
        isBlocked={!vatPeriod.has_ended}
        isCompleted={vatPeriod.is_reported}
      />

      {showEuRecapStep && (
        <StepBlock
          title={recapTitle}
          descBlocked={t('vatPage.euRecap.blocked')}
          descCurrent={<RecapDescription isOverdue={recapState === 'overdue'} />}
          descComplete={<BodyText $dimmed>{t('vatPage.euRecap.hasBeenReported')}</BodyText>}
          status={recapStatus}
        />
      )}

      <StepBlock
        isLast={true}
        title={t('vatPage.lock')}
        descCurrent={<LockDescription />}
        descComplete={<BodyText $dimmed>{t('vatPage.periodIsLocked')}</BodyText>}
        isBlocked={!vatPeriod.is_reported}
        isCompleted={vatPeriod.is_locked}
      />

      <ModalV2
        isVisible={showReportModal}
        header={
          useElsterModal ? (
            <ElsterModalHeader>
              <ElsterLogo variant="tagline" height={26} />
              <BetaPill label={t('general.beta.label')} tooltip={t('general.beta.tooltip')} />
            </ElsterModalHeader>
          ) : (
            t('vatPage.vatReport.header')
          )
        }
        handleOnClose={() => setShowReportModal(false)}
        width={430}
        height={700}
      >
        {useElsterModal ? (
          <ElsterReportModal onClose={() => setShowReportModal(false)} vatPeriod={vatPeriod} />
        ) : (
          <VatReportModal onClose={() => setShowReportModal(false)} vatPeriod={vatPeriod} />
        )}
      </ModalV2>

      <ModalV2
        isVisible={showEuRecapModal}
        header={t('vatPage.euRecap.modal.header')}
        handleOnClose={() => setShowEuRecapModal(false)}
        width={480}
        autoHeight
      >
        <EuRecapReportModal onClose={() => setShowEuRecapModal(false)} vatPeriod={vatPeriod} />
      </ModalV2>
    </>
  )
}

const DescriptionBlock = styled.div``

const DescriptionText = styled.div`
  margin-bottom: 8px;
`

const DescriptionColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const ButtonRow = styled.div<{ $align: 'left' | 'right' }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'flex-start')};
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin: 4px 0;
`

const BodyText = styled.span<{ $dimmed?: boolean }>`
  font-size: 14px;
  color: ${({ $dimmed, theme }) => ($dimmed ? theme.colors.iconGray : theme.colors.neutralBlack)};
  line-height: 1.4;
`

const InvoiceLink = styled(Link)`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`

const ElsterModalHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
